import React, { useEffect, useRef, useState } from "react";
import Select, { components } from "react-select";
import Async from "react-select/async";

const { MenuList, ValueContainer, SingleValue, Placeholder } = components;

const InputOption = ({
  getStyles,
  Icon,
  isDisabled,
  isFocused,
  isSelected,
  children,
  innerProps,
  resetFilter,
  data,
  ...rest
}) => {
  // styles
  let bg = "transparent";
  if (isFocused) bg = "#f4f7fc";

  const style = {
    alignItems: "center",
    backgroundColor: bg,
    color: "inherit",
    display: "flex",
  };

  // Determine if the option should appear selected based on selectedValues
  const isChecked = rest.selectProps.value.some(
    (selected) => selected.value === data.value
  );

  // prop assignment
  const props = {
    ...innerProps,
    style,
  };

  useEffect(() => {
    if (resetFilter === 1) {
      rest.selectOption(null); // Deselect the option
    }
  }, [resetFilter, rest]);

  return (
    <components.Option
      {...rest}
      isDisabled={isDisabled}
      isFocused={isFocused}
      isSelected={isChecked} // Reflect checkbox state
      getStyles={getStyles}
      innerProps={props}
    >
      <input
        type="checkbox"
        checked={isChecked} // Tie the checkbox state to the selected values
        style={{ marginRight: "5px", marginTop: "0px", pointerEvents: "none" }}
      />
      {children}
    </components.Option>
  );
};

const CustomMenuList = ({ selectProps, ...props }) => {
  const { onChangeSearch, inputValue, onMenuInputFocus, onInputChange } =
    selectProps;

  // Copied from source
  const ariaAttributes = {
    "aria-autocomplete": "list",
    "aria-label": selectProps["aria-label"],
    "aria-labelledby": selectProps["aria-labelledby"],
  };

  return (
    <div>
      <input
        style={{
          width: "100%",
          boxSizing: "border-box",
          padding: 10,
          border: "none",
          borderBottom: "1px solid lightgrey",
        }}
        autoCorrect="off"
        autoComplete="off"
        spellCheck="false"
        type="text"
        value={inputValue}
        onChange={(e) => {
          if (onChangeSearch) {
            return onChangeSearch(e.currentTarget.value, {
              action: "input-change",
            });
          }
          return onInputChange(e.currentTarget.value, {
            action: "input-change",
          });
        }}
        onMouseDown={(e) => {
          e.stopPropagation();
          e.target.focus();
        }}
        onTouchEnd={(e) => {
          e.stopPropagation();
          e.target.focus();
        }}
        onFocus={onMenuInputFocus}
        placeholder="Search..."
        {...ariaAttributes}
      />
      <MenuList {...props} {...selectProps} />
    </div>
  );
};

const CustomValueContainer = ({ children, selectProps, ...props }) => {
  const commonProps = {
    cx: props.cx,
    clearValue: props.clearValue,
    getStyles: props.getStyles,
    getValue: props.getValue,
    hasValue: props.hasValue,
    isMulti: props.isMulti,
    isRtl: props.isRtl,
    options: props.options,
    selectOption: props.selectOption,
    setValue: props.setValue,
    selectProps,
    theme: props.theme,
  };

  return (
    <ValueContainer {...props} selectProps={selectProps}>
      {React.Children.map(children, (child) => {
        return child ? (
          child
        ) : props.hasValue ? (
          <SingleValue
            {...commonProps}
            isFocused={selectProps.isFocused}
            isDisabled={selectProps.isDisabled}
          >
            {selectProps.getOptionLabel(props.getValue()[0])}
          </SingleValue>
        ) : (
          <Placeholder
            {...commonProps}
            key="placeholder"
            isDisabled={selectProps.isDisabled}
            data={props.getValue()}
          >
            {selectProps.placeholder}
          </Placeholder>
        );
      })}
    </ValueContainer>
  );
};

const MoreSelectedBadge = ({ items }) => {
  const style = {
    borderRadius: "4px",
    fontFamily: "Poppins-regular",
    fontSize: "14px",
  };

  const title = items.join(", ");
  const length = items.length;
  const label = `+ ${length} selected`;
  return (
    <div style={style} title={title}>
      {label}
    </div>
  );
};

const MultiValue = ({ index, getValue, resetFilter, ...props }) => {
  const maxToShow = 0;
  const overflow = getValue()
    .slice(maxToShow)
    .map((x) => x.label);

  return index < maxToShow ? (
    <components.MultiValue {...props} />
  ) : index === maxToShow ? (
    <MoreSelectedBadge items={overflow} />
  ) : null;
};

const SingleSelect = (props) => {
  const containerRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedValues, setSelectedValues] = useState([]);
  const [formData, setFormData] = useState({});
  const refValue = useRef("");

  const onDomClick = (e) => {
    if (containerRef.current && containerRef.current.contains(e.target)) {
      setIsFocused(true);
    } else {
      setIsFocused(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", onDomClick);
    return () => {
      document.removeEventListener("mousedown", onDomClick);
    };
  }, []);

  const handleChange = (values) => {
    if (!values || values.length === 0) refValue.current = "";
    const updatedFormData = { ...formData };
    updatedFormData[props.field_name] =
      values && Array.isArray(values) ? values.map((item) => item.value) : [];
    setFormData(updatedFormData);
    props.getSelectedData(updatedFormData);
    setSelectedValues(values || []);
  };

  useEffect(() => {
    if (props.resetFilter) {
      resetSelection();
    }
  }, [props.resetFilter]);

  const resetSelection = () => {
    setSelectedValues([]);
    setInputValue("");
    props.getSelectedData({ [props.field_name]: [] });
  };

  const searchHandler = (val) => {
    setInputValue(val);
  };

  return (
    <div ref={containerRef}>
      {props.async === "true" ? (
        <Async
          cacheOptions
          hideSelectedOptions={false}
          closeMenuOnSelect={false}
          defaultOptions
          value={selectedValues}
          className="multi-select"
          classNamePrefix="multi-select"
          loadOptions={(evt) => props.feedbackAsyncCall(evt)}
          isClearable
          isSearchable={false}
          isMulti
          onMenuInputFocus={() => setIsFocused(true)}
          onChange={handleChange}
          onInputChange={(val) => searchHandler(val)}
          components={{
            MenuList: CustomMenuList,
            Option: (props) => <InputOption {...props} resetFilter={props.resetFilter} />,
            MultiValue: (props) => <MultiValue {...props} resetFilter={props.resetFilter} />,
          }}
          {...{
            menuIsOpen: isFocused || undefined,
            isFocused: isFocused || undefined,
          }}
        />
      ) : (
        <Select
          className="basic-single"
          classNamePrefix="select"
          options={props.options}
          hideSelectedOptions={false}
          closeMenuOnSelect={false}
          value={selectedValues}
          isMulti
          inputValue={inputValue}
          isSearchable={false}
          onMenuInputFocus={() => setIsFocused(true)}
          onChange={handleChange}
          onInputChange={(val) => searchHandler(val)}
          components={{
            MenuList: CustomMenuList,
            Option: (props) => <InputOption {...props} resetFilter={props.resetFilter} />,
            MultiValue: (props) => <MultiValue {...props} resetFilter={props.resetFilter} />,
          }}
          {...{
            menuIsOpen: isFocused || undefined,
            isFocused: isFocused || undefined,
          }}
        />
      )}
    </div>
  );
};

export default SingleSelect;



// WEBPACK FOOTER //
// ./src/components/multiselectcustom/ReactMultiSelectCustomSearch.js